import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CalendarDays, SearchX } from 'lucide-react';
import { toast } from 'sonner';
import { HomeNav } from '../components/home/HomeNav';
import { VenueCardGrid } from '../components/home/VenueCardGrid';
import { HomeFooter, PartnerDialog } from '../components/home/HomeSections';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrowseVenuesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [venues, setVenues] = useState([]);
  const [nights, setNights] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showPartner, setShowPartner] = useState(false);
  const [showAssist, setShowAssist] = useState(false);
  const [assistForm, setAssistForm] = useState({ name: '', phone: '' });
  const [dates, setDates] = useState({
    check_in: searchParams.get('check_in') || '',
    check_out: searchParams.get('check_out') || ''
  });

  const checkIn = searchParams.get('check_in');
  const checkOut = searchParams.get('check_out');
  const hasDates = !!(checkIn && checkOut);

  const fetchVenues = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (checkIn && checkOut) { params.check_in = checkIn; params.check_out = checkOut; }
      if (searchParams.get('location')) params.location = searchParams.get('location');
      const res = await axios.get(`${API_URL}/venues/cards`, { params });
      setVenues(res.data.venues || []);
      setNights(res.data.nights || 0);
    } catch {
      setVenues([]);
    } finally {
      setIsLoading(false);
    }
  }, [checkIn, checkOut, searchParams]);

  useEffect(() => { fetchVenues(); }, [fetchVenues]);

  const applyDates = () => {
    if (!dates.check_in || !dates.check_out) return;
    if (dates.check_out <= dates.check_in) { toast.error('Check-out must be after check-in'); return; }
    setSearchParams({ check_in: dates.check_in, check_out: dates.check_out });
  };

  const clearDates = () => {
    setDates({ check_in: '', check_out: '' });
    setSearchParams({});
  };

  const requestAssistance = async () => {
    if (!assistForm.name || !assistForm.phone) { toast.error('Please enter your name and phone'); return; }
    try {
      await axios.post(`${API_URL}/leads/help-request`, null, {
        params: { phone: assistForm.phone, user_name: assistForm.name, date: hasDates ? `${checkIn} to ${checkOut}` : null, type: 'availability_assistance' }
      });
      toast.success('Thanks! The WVC team will help you find the right venue.');
      setShowAssist(false);
    } catch {
      toast.error('Failed to submit, please try again.');
    }
  };

  const availableCount = venues.filter(v => v.availability_status === 'available').length;
  const noPerfectMatch = hasDates && !isLoading && availableCount === 0;

  return (
    <div className="min-h-screen bg-[#FDFCF8] font-wvc-body" data-testid="browse-venues-page">
      <div className="bg-[#1A3626] pb-10">
        <HomeNav dark onPartner={() => setShowPartner(true)} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-28">
          <h1 className="font-wvc-display text-3xl md:text-5xl text-white font-medium tracking-tight" data-testid="browse-heading">
            {hasDates ? `Venues for ${checkIn} → ${checkOut}` : 'Browse All Wedding Venues'}
          </h1>
          <p className="text-white/70 mt-3 text-sm md:text-base">
            {hasDates
              ? `${nights} night${nights > 1 ? 's' : ''} · ${availableCount} venue${availableCount !== 1 ? 's' : ''} available · availability from venue-updated calendars`
              : 'Explore venues with transparent availability, pricing and venue-specific calculators. No login needed.'}
          </p>
          <div className="mt-6 flex flex-wrap items-end gap-3 bg-white/10 backdrop-blur rounded-2xl p-4 max-w-2xl" data-testid="browse-date-bar">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 flex items-center gap-1 mb-1"><CalendarDays className="w-3 h-3" strokeWidth={1.5} /> Check-in</label>
              <input type="date" value={dates.check_in} onChange={e => setDates(p => ({ ...p, check_in: e.target.value }))}
                className="h-11 rounded-lg border-0 bg-white px-3 text-sm text-[#1A201C]" data-testid="browse-checkin-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 flex items-center gap-1 mb-1"><CalendarDays className="w-3 h-3" strokeWidth={1.5} /> Check-out</label>
              <input type="date" min={dates.check_in} value={dates.check_out} onChange={e => setDates(p => ({ ...p, check_out: e.target.value }))}
                className="h-11 rounded-lg border-0 bg-white px-3 text-sm text-[#1A201C]" data-testid="browse-checkout-input" />
            </div>
            <button onClick={applyDates} className="h-11 bg-[#C6A87C] text-[#1A201C] hover:bg-[#E5D8C6] rounded-full px-6 text-sm font-semibold transition-colors" data-testid="browse-apply-dates">
              Check Availability
            </button>
            {hasDates && (
              <button onClick={clearDates} className="h-11 text-white/70 hover:text-white text-sm underline underline-offset-4" data-testid="browse-clear-dates">
                Clear dates
              </button>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {isLoading ? (
          <div className="py-24 text-center">
            <div className="w-10 h-10 border-3 border-[#1A3626]/20 border-t-[#1A3626] rounded-full animate-spin mx-auto" />
            <p className="text-[#748278] text-sm mt-4">Checking venue calendars...</p>
          </div>
        ) : venues.length === 0 ? (
          <div className="py-24 text-center" data-testid="browse-empty-state">
            <SearchX className="w-10 h-10 text-[#748278] mx-auto" strokeWidth={1.5} />
            <p className="font-wvc-display text-2xl text-[#1A201C] mt-4">No venues onboarded yet</p>
            <p className="text-[#3E4C43] mt-2">New venues are being added. Check back soon.</p>
          </div>
        ) : (
          <>
            {noPerfectMatch && (
              <div className="bg-[#F3F1E7] rounded-2xl p-8 mb-10 text-center" data-testid="no-availability-state">
                <p className="font-wvc-display text-2xl md:text-3xl text-[#1A201C]">We Couldn't Find a Perfect Match for These Dates</p>
                <p className="text-[#3E4C43] mt-3 max-w-xl mx-auto">Try nearby dates, explore the venues below or let the WVC team help you find another option.</p>
                <div className="flex flex-wrap justify-center gap-3 mt-6">
                  <button onClick={clearDates} className="bg-[#1A3626] text-white hover:bg-[#254834] rounded-full px-6 py-3 text-sm transition-colors" data-testid="no-availability-browse-all">
                    Browse All Venues
                  </button>
                  <button onClick={() => setShowAssist(true)} className="border border-[#1A3626] text-[#1A3626] hover:bg-white rounded-full px-6 py-3 text-sm transition-colors" data-testid="no-availability-assist">
                    Request Assistance
                  </button>
                </div>
              </div>
            )}
            <VenueCardGrid venues={venues} settings={{}} />
          </>
        )}
      </main>

      <HomeFooter />
      <PartnerDialog open={showPartner} onOpenChange={setShowPartner} />
      <Dialog open={showAssist} onOpenChange={setShowAssist}>
        <DialogContent className="sm:max-w-sm font-wvc-body">
          <DialogHeader>
            <DialogTitle className="font-wvc-display text-2xl">Let WVC Help</DialogTitle>
            <DialogDescription>Share your details and our team will find venue options for your dates.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input placeholder="Your name *" value={assistForm.name} onChange={e => setAssistForm(p => ({ ...p, name: e.target.value }))} data-testid="assist-name-input" />
            <Input placeholder="Phone *" value={assistForm.phone} onChange={e => setAssistForm(p => ({ ...p, phone: e.target.value }))} data-testid="assist-phone-input" />
            <Button onClick={requestAssistance} className="w-full bg-[#1A3626] hover:bg-[#254834] rounded-full" data-testid="assist-submit-btn">Request Assistance</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
